import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon, TrashIcon } from '@radix-ui/react-icons';
import { memo, useCallback, useEffect, useId, useMemo, useState } from 'react';

import { Badge } from '../../primitives/badge';
import { Button } from '../../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../primitives/dropdown-menu';
import { CornerDownRightIcon } from '../../svg/corner-down-right-icon';
import { cn } from '../../utils/cn';
import { ComboboxModel } from '../combobox/combobox-model';
import { ListNotionItem } from '../list/list-notion-item';
import { OPERATOR, OPERATORS } from './constants';
import { CreateCustomFactSheet } from './create-custom-fact-sheet';
import { CreateCustomPathSheet } from './create-custom-path-sheet';
import { FACT_TYPE_OPTIONS, PATH_TYPE_OPTIONS } from './fact-type-options';
import { getValueCase, isValueCompatible, isValueEmpty, ValueInput } from './inputs/value-input';
import { useRuleBuilderDisabled, useRuleBuilderStore } from './store';
import type { TConditionNode, TFact, TObjectPath } from './types';
import {
  isDateMathMissingBaseDate,
  isLockedBaseSegmentCondition,
  normalizePathForUI,
  normalizePathToStored,
} from './utils';

type TConditionProps = {
  /** The condition node data to render. */
  node: TConditionNode;
  /** Available facts for the fact selector dropdown. */
  facts: TFact[];
  /** Names of facts that were created as custom facts in this RuleBuilder instance. */
  customFactNames: Set<string>;
  /** ID of the parent group that owns this condition. */
  parentGroupId: string;
  /** Names of facts currently used by at least one condition in the rule tree. */
  usedFactNames: Set<string>;
  /** Deletes a custom fact from the current RuleBuilder instance. */
  onDeleteCustomFact: (factName: string) => void;
  /** Object-path options for the currently selected fact. */
  objectPaths: TObjectPath[];
  /** Object paths currently used by at least one condition for the selected fact. */
  usedObjectPaths: Set<string>;
  /** Registers a custom object path for the selected fact. */
  onCreateObjectPath: (factName: string, objectPath: TObjectPath) => void;
  /** Deletes a custom object path from the selected fact path catalog. */
  onDeleteObjectPath: (factName: string, path: string) => void;
  /** When true, deletion uses a dropdown confirmation step. When false, deletes immediately. */
  requireConfirmation?: boolean;
  /** Registers a custom fact in the current RuleBuilder instance so all conditions can reuse it. */
  onCreateCustomFact: (customFact: TFact) => void;
};

/**
 * Condition
 *
 * Renders a single condition row with three fields:
 * - **Fact**: the attribute to evaluate (combobox).
 * - **Operator**: the comparison operator filtered by the fact's data type (combobox).
 * - **Value**: the target value to compare against (text input).
 *
 * Supports drag-and-drop reordering via `@dnd-kit/sortable` and highlights
 * invalid fields when validation is triggered from the parent `RuleBuilder`.
 */
export const Condition = memo(function Condition({
  node,
  facts,
  customFactNames,
  parentGroupId,
  usedFactNames,
  onDeleteCustomFact,
  objectPaths,
  usedObjectPaths,
  onCreateObjectPath,
  onDeleteObjectPath,
  requireConfirmation = true,
  onCreateCustomFact,
}: TConditionProps) {
  const [isCreateFactDrawerOpen, setIsCreateFactDrawerOpen] = useState(false);
  const [initialCustomFactValue, setInitialCustomFactValue] = useState('');
  const [isCreatePathDrawerOpen, setIsCreatePathDrawerOpen] = useState(false);
  const [initialCustomPathValue, setInitialCustomPathValue] = useState('');
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);

  const updateCondition = useRuleBuilderStore((s) => s.updateCondition);
  const deleteNode = useRuleBuilderStore((s) => s.deleteNode);
  const isNodeInvalid = useRuleBuilderStore(
    (s) => s.validation.showValidation && s.validation.invalidNodeIds.has(node.id),
  );
  const kind = useRuleBuilderStore((s) => s.kind);
  const rootId = useRuleBuilderStore((s) => s.root.id);
  const rootHasAnotherVariantSchemaCondition = useRuleBuilderStore((s) =>
    s.root.children.some(
      (child) =>
        child.type === 'condition' &&
        child.operator === OPERATOR.variantSchema &&
        child.id !== node.id,
    ),
  );
  const disabled = useRuleBuilderDisabled();
  const isLockedBaseSegment = isLockedBaseSegmentCondition(node, kind);
  const isInteractionDisabled = disabled || isLockedBaseSegment;
  const isVariantSchemaDeleteLocked =
    kind === 'rule-with-variants' && node.operator === OPERATOR.variantSchema;
  const isObjectFactWithoutPathType =
    node.factType?.toLowerCase().trim() === 'object' && !node.pathType;
  const isOperatorDisabled =
    isInteractionDisabled || isVariantSchemaDeleteLocked || isObjectFactWithoutPathType;

  const allFacts = facts;

  const allowedFacts = useMemo(() => {
    if (!isVariantSchemaDeleteLocked) return allFacts;

    return allFacts.filter((fact) => {
      const codeDataType = fact.codeDataType?.toLowerCase();
      return codeDataType === 'string' || codeDataType === 'number';
    });
  }, [allFacts, isVariantSchemaDeleteLocked]);

  // useId() is stable per component instance, not per node.id.
  // This prevents duplicate id attributes when AnimatePresence keeps both the
  // exit (source group) and enter (destination group) instances in the DOM
  // simultaneously during a move animation.
  const uid = useId();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled: isInteractionDisabled,
    data: { parentGroupId, nodeType: 'condition' },
  });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeleteFactOption = useCallback(
    (factName: string) => {
      if (usedFactNames.has(factName)) return;
      onDeleteCustomFact(factName);
    },
    [onDeleteCustomFact, usedFactNames],
  );

  /** Combobox model built from the available facts list. */
  const factsComboboxModel = useMemo(
    () =>
      allowedFacts.map((fact) => ({
        key: fact.attributeName,
        value: fact.attributeName,
        button:
          customFactNames.has(fact.attributeName) && !usedFactNames.has(fact.attributeName)
            ? {
                text: 'Eliminar',
                onClick: () => handleDeleteFactOption(fact.attributeName),
              }
            : undefined,
        item: (
          <span className="flex w-full items-center justify-between gap-1">
            <span className="grid">
              <span title={fact.attributeName} className="truncate">
                {fact.attributeName}
              </span>
            </span>
            {fact.codeDataType && (
              <Badge className="shrink-0 p-0 px-1.5 text-[10px]" variant="warning">
                {fact.codeDataType}
              </Badge>
            )}
          </span>
        ),
      })),
    [allowedFacts, customFactNames, usedFactNames, handleDeleteFactOption],
  );

  const objectPathsComboboxModel = useMemo(
    () =>
      objectPaths.map((entry) => ({
        value: entry.path,
        key: normalizePathForUI(entry.path),
        button: !usedObjectPaths.has(entry.path)
          ? {
              text: 'Eliminar',
              onClick: () => onDeleteObjectPath(node.fact, entry.path),
            }
          : undefined,
        item: (
          <span className="flex w-full items-center justify-between gap-1">
            <span title={normalizePathForUI(entry.path)} className="truncate">
              {normalizePathForUI(entry.path)}
            </span>
            <Badge className="shrink-0 p-0 px-1.5 text-[10px]" variant="warning">
              {entry.pathType}
            </Badge>
          </span>
        ),
      })),
    [node.fact, objectPaths, onDeleteObjectPath, usedObjectPaths],
  );

  const getAllowedOperatorsForFact = useCallback(
    (factName: string, params?: { factType?: string; pathType?: string }) => {
      const selectedFact = allFacts.find((fact) => fact.attributeName === factName);
      const selectedFactType =
        params?.factType?.toLowerCase().trim() || selectedFact?.codeDataType?.toLowerCase() || '';
      if (selectedFactType === 'object' && !params?.pathType?.trim()) {
        return [];
      }
      const factDataType =
        selectedFactType === 'object'
          ? params?.pathType?.toLowerCase().trim() || ''
          : selectedFactType;
      const isRootCondition = parentGroupId === rootId;
      const hideSegmentOperator = kind === 'rule-with-variants';
      const canUseVariantSchema =
        kind === 'rule-with-variants' &&
        isRootCondition &&
        (!rootHasAnotherVariantSchemaCondition || node.operator === OPERATOR.variantSchema);

      const filteredOperators = factDataType
        ? OPERATORS.filter((op) => op.type.includes(factDataType))
        : OPERATORS;

      return filteredOperators.filter((op) => {
        if (op.value === OPERATOR.segment && hideSegmentOperator) return false;
        if (op.value === OPERATOR.variantSchema) return canUseVariantSchema;
        return true;
      });
    },
    [allFacts, kind, node.operator, parentGroupId, rootHasAnotherVariantSchemaCondition, rootId],
  );

  /** Combobox model for operators, filtered by fact type and variant-schema rules. */
  const operatorsComboboxModel = useMemo(() => {
    const allowedOperators = getAllowedOperatorsForFact(node.fact, {
      factType: node.factType,
      pathType: node.pathType,
    });
    return allowedOperators.map((op) => ({ value: op.value, item: op.label }));
  }, [getAllowedOperatorsForFact, node.fact, node.factType, node.pathType]);

  const getFactTypeValue = useCallback(
    (factName: string) => {
      const selectedFact = allFacts.find((fact) => fact.attributeName === factName);
      const codeDataType = selectedFact?.codeDataType?.trim();
      return codeDataType || undefined;
    },
    [allFacts],
  );

  // Keep factType aligned with the selected fact datatype, even for legacy conditions
  // that were created before factType existed.
  useEffect(() => {
    if (!node.fact) return;
    const nextFactType = getFactTypeValue(node.fact);
    if (!nextFactType || node.factType === nextFactType) return;
    updateCondition(node.id, { factType: nextFactType });
  }, [getFactTypeValue, node.fact, node.factType, node.id, updateCondition]);

  useEffect(() => {
    if (node.factType?.toLowerCase().trim() !== 'object') return;
    if (!node.path) return;
    const normalizedPath = normalizePathToStored(node.path);
    if (normalizedPath && normalizedPath !== node.path) {
      updateCondition(node.id, { path: normalizedPath });
    }
  }, [node.factType, node.id, node.path, updateCondition]);

  useEffect(() => {
    if (node.factType?.toLowerCase().trim() !== 'object') return;
    if (!node.pathType && node.operator) {
      updateCondition(node.id, { operator: '', value: '' });
    }
  }, [node.factType, node.id, node.operator, node.pathType, updateCondition]);

  const isFactMissing = isNodeInvalid && !node.fact;
  const isOperatorMissing = isNodeInvalid && !node.operator;
  const isObjectPathRequired =
    (node.factType?.toLowerCase().trim() ||
      allFacts.find((f) => f.attributeName === node.fact)?.codeDataType?.toLowerCase() ||
      '') === 'object';
  const isPathMissing =
    isNodeInvalid && isObjectPathRequired && !normalizePathToStored(node.path ?? '');
  const isPathTypeMissing =
    isNodeInvalid && isObjectPathRequired && (!node.pathType || node.pathType === 'object');
  const isValueMissing =
    isNodeInvalid && (isValueEmpty(node.value) || isDateMathMissingBaseDate(node.value));

  // True when the fact was saved in the past but no longer exists in the current facts list.
  const isFactObsolete = !!node.fact && !allFacts.some((f) => f.attributeName === node.fact);
  const isVariantSchemaCondition = node.operator === OPERATOR.variantSchema;

  const selectedFactDataType =
    node.factType?.toLowerCase() ??
    allFacts.find((f) => f.attributeName === node.fact)?.codeDataType?.toLowerCase() ??
    '';
  const effectiveDataTypeForValue =
    selectedFactDataType === 'object' ? (node.pathType?.toLowerCase() ?? '') : selectedFactDataType;
  const hasNoValueInput = getValueCase(effectiveDataTypeForValue, node.operator) === 'NO_INPUT';

  const handleFactSelect = useCallback(
    (selectedFact: string) => {
      const factType = getFactTypeValue(selectedFact);
      const normalizedFactType = factType?.toLowerCase().trim() ?? '';
      const isObjectFactType = normalizedFactType === 'object';
      const nextPath = isObjectFactType ? '' : undefined;
      const nextPathType = isObjectFactType ? '' : undefined;
      const factTypeUpdate = {
        ...(factType ? { factType } : {}),
        ...(nextPath !== undefined ? { path: nextPath } : { path: undefined }),
        ...(nextPathType !== undefined ? { pathType: nextPathType } : { pathType: undefined }),
      };

      if (isVariantSchemaDeleteLocked) {
        updateCondition(node.id, { fact: selectedFact, ...factTypeUpdate });
        return;
      }

      const isOperatorStillValid =
        node.operator !== '' &&
        getAllowedOperatorsForFact(selectedFact, {
          factType,
          pathType: isObjectFactType ? '' : undefined,
        }).some((op) => op.value === node.operator);
      if (!isOperatorStillValid) {
        updateCondition(node.id, {
          fact: selectedFact,
          operator: '',
          value: '',
          ...factTypeUpdate,
        });
        return;
      }
      const prevFactDataType = (
        node.factType ??
        allFacts.find((f) => f.attributeName === node.fact)?.codeDataType ??
        ''
      )
        .toLowerCase()
        .trim();
      const newFactDataType = (
        factType ??
        allFacts.find((f) => f.attributeName === selectedFact)?.codeDataType ??
        ''
      )
        .toLowerCase()
        .trim();
      const nextDataTypeForValue = newFactDataType === 'object' ? '' : newFactDataType;
      const newValueCase = getValueCase(nextDataTypeForValue, node.operator);
      const isValueStillCompatible = isValueCompatible(node.value, newValueCase);
      const shouldResetValue = !isValueStillCompatible || prevFactDataType !== 'date';
      updateCondition(node.id, {
        fact: selectedFact,
        ...factTypeUpdate,
        ...(isObjectFactType || shouldResetValue ? { value: '' } : {}),
        ...(isObjectFactType ? { operator: '' } : {}),
      });
    },
    [
      node.id,
      node.fact,
      node.operator,
      node.factType,
      node.value,
      allFacts,
      getAllowedOperatorsForFact,
      getFactTypeValue,
      isVariantSchemaDeleteLocked,
      updateCondition,
    ],
  );

  const handleOperatorSelect = useCallback(
    (selectedOperator: string) => {
      if (isVariantSchemaDeleteLocked) return;
      updateCondition(node.id, { operator: selectedOperator });
    },
    [isVariantSchemaDeleteLocked, node.id, updateCondition],
  );

  const handleValueChange = useCallback(
    (value: unknown) => updateCondition(node.id, { value }),
    [node.id, updateCondition],
  );

  const handleValueFactChange = useCallback(
    (fact: string) => {
      const factType = getFactTypeValue(fact);
      updateCondition(node.id, {
        fact,
        ...(factType ? { factType } : {}),
      });
    },
    [getFactTypeValue, node.id, updateCondition],
  );

  const handlePathSelect = useCallback(
    (selectedPath: string) => {
      const selectedPathEntry = objectPaths.find((entry) => entry.path === selectedPath);
      const nextPathType = selectedPathEntry?.pathType ?? '';
      const isOperatorStillValid =
        node.operator !== '' &&
        getAllowedOperatorsForFact(node.fact, {
          factType: node.factType,
          pathType: nextPathType,
        }).some((op) => op.value === node.operator);

      updateCondition(node.id, {
        path: selectedPath,
        pathType: nextPathType,
        ...(isOperatorStillValid ? {} : { operator: '', value: '' }),
      });
    },
    [
      getAllowedOperatorsForFact,
      node.fact,
      node.factType,
      node.id,
      node.operator,
      objectPaths,
      updateCondition,
    ],
  );

  const handleCreateObjectPath = useCallback(
    (customPath: TObjectPath) => {
      if (!node.fact) return;
      onCreateObjectPath(node.fact, customPath);
      updateCondition(node.id, {
        path: customPath.path,
        pathType: customPath.pathType,
        operator: '',
        value: '',
      });
    },
    [node.fact, node.id, onCreateObjectPath, updateCondition],
  );

  const handleDelete = useCallback(() => deleteNode(node.id), [node.id, deleteNode]);

  const handleCreateCustomFact = useCallback(
    (customFact: TFact) => {
      onCreateCustomFact(customFact);
      handleFactSelect(customFact.attributeName);
    },
    [handleFactSelect, onCreateCustomFact],
  );

  const factTypeOptions = useMemo(
    () =>
      isVariantSchemaDeleteLocked
        ? FACT_TYPE_OPTIONS.filter((type) => type === 'string' || type === 'number')
        : FACT_TYPE_OPTIONS,
    [isVariantSchemaDeleteLocked],
  );

  return (
    <div ref={setNodeRef} style={dragStyle}>
      {isVariantSchemaCondition && (
        <Badge variant="warning" className="ml-4 rounded-b-none px-8 pt-2">
          Configura el número de variantes y su rango
        </Badge>
      )}

      {!isNodeInvalid && isLockedBaseSegment && (
        <Badge variant="success" className="ml-4 rounded-b-none px-8 pt-2">
          Segmentación de la variante
        </Badge>
      )}

      <div
        className={cn(
          'm-[1px] flex items-start justify-between gap-2 rounded-lg border border-input bg-background/50 p-2 shadow-xs transition-shadow',
          isVariantSchemaCondition && 'border-warning bg-warning/10',
          hasNoValueInput ? 'min-h-[3.6rem]' : 'min-h-[6rem]',
          isDragging && 'pointer-events-none opacity-30',
          isNodeInvalid && 'border-destructive bg-destructive/5',
          !isNodeInvalid && isLockedBaseSegment && 'border-success bg-success/10',
          !isNodeInvalid && isFactObsolete && 'border-warning bg-warning/5',
          isDeleteConfirmationOpen && 'border-dashed border-destructive bg-destructive/5',
        )}
      >
        {!isInteractionDisabled && (
          <Button
            aria-label="Arrastrar condición"
            className="flex-none cursor-grab touch-none"
            size="icon-sm"
            title="Arrastrar condición"
            variant="ghost"
            {...attributes}
            {...listeners}
          >
            <DragHandleDots2Icon className="size-4" />
          </Button>
        )}

        <div className="grid w-full gap-2 lg:gap-2">
          <div className="grid w-full items-center gap-2 lg:grid-cols-2 lg:gap-4">
            <div
              className={cn('flex flex-col gap-1', {
                'rounded-[1.2rem] bg-muted p-1.5': isObjectPathRequired,
              })}
            >
              <ComboboxModel
                button={{
                  text: 'Crear',
                  onClick: (searchValue) => {
                    setInitialCustomFactValue(searchValue);
                    setIsCreateFactDrawerOpen(true);
                  },
                }}
                className={cn({
                  'border-destructive': isFactMissing,
                  'border-warning': isFactObsolete,
                })}
                disabled={isInteractionDisabled}
                id={`${uid}-fact`}
                model={factsComboboxModel}
                placeholder="Seleccionar fact"
                size="xs"
                value={node.fact}
                variant="ghost"
                onSelect={handleFactSelect}
              />

              {isObjectPathRequired && (
                <div className="flex w-full min-w-0 items-center gap-1">
                  <div className="flex-none pb-1 pl-3">
                    <CornerDownRightIcon
                      className={cn('size-5 text-accent', {
                        'opacity-50': isInteractionDisabled,
                      })}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <ComboboxModel
                      button={{
                        text: 'Crear',
                        onClick: (searchValue) => {
                          setInitialCustomPathValue(normalizePathForUI(searchValue));
                          setIsCreatePathDrawerOpen(true);
                        },
                      }}
                      className={cn('w-full min-w-0', {
                        'border-destructive': isPathMissing || isPathTypeMissing,
                      })}
                      disabled={isInteractionDisabled || !node.fact}
                      id={`${uid}-path`}
                      model={objectPathsComboboxModel}
                      placeholder={{
                        text: 'Seleccionar path',
                        className:
                          'text-accent group-aria-expanded:text-foreground group-hover:text-foreground',
                      }}
                      emptyOptionsText={{
                        text: 'Seleccionar path',
                        className:
                          'text-accent group-aria-expanded:text-foreground group-hover:text-foreground',
                      }}
                      size="xs"
                      value={node.path}
                      variant="ghost"
                      onSelect={handlePathSelect}
                    />
                  </div>
                </div>
              )}
            </div>

            <ComboboxModel
              className={cn('bg-accent/20', {
                'border-destructive': isOperatorMissing,
              })}
              disabled={isOperatorDisabled}
              id={`${uid}-operator`}
              model={operatorsComboboxModel}
              placeholder="Operador"
              size="xs"
              value={node.operator}
              variant="ghost"
              onSelect={handleOperatorSelect}
            />
          </div>

          <div className="w-full">
            <ValueInput
              disabled={disabled}
              readonly={isLockedBaseSegment}
              fact={node.fact}
              factType={node.factType}
              facts={allowedFacts}
              id={`${uid}-value`}
              isValueMissing={isValueMissing}
              operator={node.operator}
              pathType={node.pathType}
              value={node.value}
              onChange={handleValueChange}
              onFactChange={handleValueFactChange}
            />
          </div>

          {isFactObsolete && (
            <p className="text-xs text-warning">
              El fact <span className="font-semibold">"{node.fact}"</span> no existe en la lista de
              facts disponibles. Los resultados de la evaluación podrían ser{' '}
              <span className="font-semibold">inestables</span>.
            </p>
          )}

          <CreateCustomFactSheet
            existingFacts={allFacts}
            factTypeOptions={factTypeOptions}
            initialFact={initialCustomFactValue}
            open={isCreateFactDrawerOpen}
            onOpenChange={setIsCreateFactDrawerOpen}
            onSave={handleCreateCustomFact}
          />

          <CreateCustomPathSheet
            existingPaths={objectPaths}
            pathTypeOptions={PATH_TYPE_OPTIONS}
            initialPath={initialCustomPathValue}
            open={isCreatePathDrawerOpen}
            onOpenChange={setIsCreatePathDrawerOpen}
            onSave={handleCreateObjectPath}
          />
        </div>

        {!isInteractionDisabled &&
          !isVariantSchemaDeleteLocked &&
          (requireConfirmation ? (
            <DropdownMenu onOpenChange={setIsDeleteConfirmationOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  aria-label="Eliminar condición"
                  className="flex-none opacity-50"
                  size="icon-sm"
                  title="Eliminar condición"
                  variant="ghost"
                >
                  <TrashIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[10rem]" side="left" align="center">
                <DropdownMenuItem onClick={handleDelete}>
                  <ListNotionItem
                    className="cursor-pointer text-destructive"
                    description="Clic para confirmar"
                    title="Eliminar condición"
                    truncate={false}
                  />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              aria-label="Eliminar condición"
              className="flex-none opacity-50"
              size="icon-sm"
              title="Eliminar condición"
              variant="ghost"
              onClick={handleDelete}
            >
              <TrashIcon className="size-4" />
            </Button>
          ))}
      </div>
    </div>
  );
});
